import StateObserver, { user } from 'src/StateObserver';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import {
  getSuperLollipopLastSeen,
  getSuperLollipopWinStreak,
  getSuperLollipopWinStreakRequired,
  isSuperLollipopActive,
  isSuperLollipopAvailable,
} from 'src/replicant/superLollipop/superLollipop.getters';
import { playMatch2 } from '../minigame/match2-timestep/match2.play';
import { track } from 'src/lib/analytics/utils';
import { PopupSuperLollipopActiveMode } from './popups/PopupSuperLollipopActive';

type OriginFeature = 'hud' | 'launch' | 'postPlay' | 'admin';

/**
 * Regular super lollipop sequence, that presents the info, progress and retain popups
 * Return true if the user taps to play the puzzle
 * @returns
 */
export async function openSuperLollipop(args: {
  originFeature: OriginFeature;
}) {
  const { originFeature } = args;
  const admin = originFeature === 'admin';

  // Skip if feature is not available
  if (!isSuperLollipopAvailable(user())) return false;

  if (!getSuperLollipopLastSeen(user())) {
    track('SuperLollipopFTUEView', { originFeature });
    // Start with the info popup if feature has never been seen
    await openSuperLollipopInfoPopup(originFeature);
  }

  // Update last time the feature has been seen by the user
  await StateObserver.invoke.updateSuperLollipopLastSeen();

  // Sent the open event - that can be either the progress popup or active popup
  track('SuperLollipopOpen', { originFeature });

  if (isSuperLollipopActive(user())) {
    // If is from admin message, user should not be able to play straight away
    await openSuperLollipopActivePopup({
      mode: admin ? 'tapToContinue' : 'tapToPlay',
      originFeature,
    });
    if (!admin) {
      // Always play after active popup in this case, if not from admin message
      track('SuperLollipopPlay', { originFeature });
      playMatch2({ origin: undefined });
    }
    return true;
  }

  // Show progress popup
  const play = await openSuperLollipopProgressPopup(originFeature);

  // Play the game if the user wants to play - only if not from admin message
  if (play && !admin) {
    track('SuperLollipopPlay', { originFeature });
    playMatch2({ origin: undefined });
  } else {
    track('SuperLollipopClose', { originFeature });
  }

  return play;
}

/**
 * Super lollipop sequence that runs on game launch
 * @returns
 */
export async function superLollipopLaunch() {
  // Skip if feature is not available
  if (!isSuperLollipopAvailable(user())) return false;

  // Skip if feature has been seen already
  if (getSuperLollipopLastSeen(user())) return false;

  // Show progress popup
  return await openSuperLollipop({ originFeature: 'launch' });
}

/**
 * Super lollipop sequence that runs after a game has been played
 * @param win
 * @returns
 */
export async function superLollipopPostGame(args: {
  hasWon: boolean;
  wasSuperLollipopActive?: boolean;
}) {
  // Skip if feature is not available
  if (!isSuperLollipopAvailable(user())) return;

  // Skip if user didn't win the level
  if (!args.hasWon) {
    if (args.wasSuperLollipopActive) {
      return openPopupPromise('popupSuperLollipopLost', {
        origin: 'postLevel',
      });
    }
    return;
  }

  // Skip if not exactly 10/10 streak - 10/10 means that it was just activated this level
  if (
    getSuperLollipopWinStreak(user()) !==
    getSuperLollipopWinStreakRequired(user())
  ) {
    return;
  }

  // If the feature became active, show the active popup
  if (isSuperLollipopActive(user())) {
    await openSuperLollipopActivePopup({
      mode: 'tapToContinue',
      originFeature: 'postPlay',
    });
  }
}

/**
 * Open the Super Lollipop info popup
 */
function openSuperLollipopInfoPopup(originFeature: OriginFeature) {
  return openPopupPromise('popupSuperLollipopInfo', {
    origin: mapFeatureToDialogOrigin(originFeature),
  });
}

/**
 * Open the Super Lollipop progress popup - will return true if the user taps to play from the popup
 */
function openSuperLollipopProgressPopup(originFeature: OriginFeature) {
  const maxStreak = getSuperLollipopWinStreakRequired(user());
  const currentStreak = Math.min(getSuperLollipopWinStreak(user()), maxStreak);
  return openPopupPromise('popupSuperLollipopProgress', {
    currentStreak,
    maxStreak,
    origin: mapFeatureToDialogOrigin(originFeature),
  });
}

/**
 * Open the Super Lollipop active popup
 */
function openSuperLollipopActivePopup(args: {
  mode: PopupSuperLollipopActiveMode;
  originFeature: OriginFeature;
}) {
  const { mode, originFeature } = args;
  track('SuperLollipopActivateView', { originFeature });
  return openPopupPromise('popupSuperLollipopActive', {
    mode,
    origin: mapFeatureToDialogOrigin(originFeature),
  });
}
